import React, { useState } from 'react';
import './ServiceTimeline.css';
import { Tooltip, Typography, Box } from '@mui/material';
import { ThemePalette } from 'mui.theme';
import { type HealthScore, type AssetDetails } from 'types/squares.type';

interface TimelineItemProps {
  label: string;
  services: HealthScore[];
  enableDarkTheme: boolean;
  onMonthSelect: (month: string | null) => void;
}

export const SquareServiceTimeline: React.FC<{
  services: HealthScore[];
  enableDarkTheme: boolean;
  onMonthSelect: (month: string | null) => void;
}> = ({ services, enableDarkTheme, onMonthSelect }) => {
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth();

  // Helper function to get the correct month index in UTC
  const getUTCMonth = (dateString: string) => {
    const date = new Date(dateString);
    return new Date(
      Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
    ).getUTCMonth();
  };

  // Order months to start from the current month
  const orderedMonths = [
    ...months.slice(currentMonth).map((month) => `${month} ${currentYear}`),
    ...months
      .slice(0, currentMonth)
      .map((month) => `${month} ${currentYear + 1}`),
  ];

  return (
    <div
      className={
        enableDarkTheme ? 'timeline-container-dark' : 'timeline-container'
      }
    >
      {/* Add "All" bubble to clear filters */}
      <TimelineItem
        label="All"
        services={services}
        enableDarkTheme={enableDarkTheme}
        onMonthSelect={() => {
          onMonthSelect(null);
        }} // Clears the filter
      />
      {orderedMonths.map((month, index) => (
        <TimelineItem
          key={index}
          label={month}
          services={services.filter(
            (service) =>
              getUTCMonth(service.asset_details.squares_recommended_date) ===
              (currentMonth + index) % 12
          )}
          enableDarkTheme={enableDarkTheme}
          onMonthSelect={onMonthSelect}
        />
      ))}
    </div>
  );
};

const TimelineItem: React.FC<TimelineItemProps> = ({
  label,
  services,
  enableDarkTheme,
  onMonthSelect,
}) => {
  const handleClick = () => {
    // Toggle selection of the month on click
    onMonthSelect(label === 'All' ? null : label); // Clears if "All" is clicked
  };

  return (
    <div className="timeline-item">
      <Tooltip
        title={
          services.length
            ? services
                .map((service) => service.asset_details.bumper_number)
                .join(', ')
            : 'No assets'
        } // Show bumper numbers in tooltip or "No assets" if empty
        arrow
        placement="top"
      >
        <Box
          className={
            enableDarkTheme ? 'circle circle-dark' : 'circle circle-light'
          }
          onClick={handleClick}
          sx={{ cursor: 'pointer' }} // Add pointer cursor for clickable feedback
        >
          <Typography variant="body2">{services.length}</Typography>
        </Box>
      </Tooltip>
      <div className="label">
        <Typography variant="body2">{label}</Typography>
      </div>
    </div>
  );
};

export default SquareServiceTimeline;
